/**
 * MyVault TypeScript Types
 * Schema-driven application builder types for the SaaS module
 */

// ============================================================================
// Core Schema Types
// ============================================================================

export interface Schema {
  id: number;
  workspace_id: string;
  name: string;
  display_name?: string;
  description?: string;
  columns: SchemaColumn[];
  relationships?: SchemaRelationship[];
  created_at: string;
  updated_at: string;
}

export interface SchemaColumn {
  id: number;
  table_id: number;
  name: string;
  display_name?: string;
  data_type: DataType;
  nullable: boolean;
  default_value?: unknown;
  is_primary_key: boolean;
  is_unique: boolean;
  max_length?: number;
  precision?: number;
  scale?: number;
  enum_values?: string[];
  sort_order: number;
}

export type DataType =
  | "string"
  | "text"
  | "integer"
  | "decimal"
  | "boolean"
  | "date"
  | "datetime"
  | "time"
  | "uuid"
  | "json"
  | "enum"
  | "file";

export interface SchemaRelationship {
  id: number;
  name: string;
  source_table_id: number;
  source_column_id: number;
  target_table_id: number;
  target_column_id: number;
  relationship_type: RelationshipType;
  on_delete: ReferentialAction;
  on_update: ReferentialAction;
}

export type RelationshipType =
  "one_to_one" | "one_to_many" | "many_to_one" | "many_to_many";
export type ReferentialAction =
  "CASCADE" | "SET_NULL" | "SET_DEFAULT" | "RESTRICT" | "NO_ACTION";

// Schema CRUD types
export interface SchemaCreate {
  name: string;
  display_name?: string;
  description?: string;
  columns: SchemaColumnCreate[];
}

export interface SchemaColumnCreate {
  name: string;
  display_name?: string;
  data_type: DataType;
  nullable?: boolean;
  default_value?: unknown;
  is_primary_key?: boolean;
  is_unique?: boolean;
  max_length?: number;
  precision?: number;
  scale?: number;
  enum_values?: string[];
}

export interface SchemaUpdate {
  name?: string;
  display_name?: string;
  description?: string;
}

// ============================================================================
// View Definition Types
// ============================================================================

export interface ViewDefinition {
  id: number | string;
  workspace_id: string;
  name: string;
  description?: string;
  focus_table_id: string;
  focus_table?: SchemaTableWithColumns;
  fields: ViewField[];
  joins: ViewJoin[];
  filters?: ViewFilter[];
  default_filters?: ViewDefaultFilter[];
  permissions?: ViewPermissionRow[];
  default_sort?: ViewSort[];
  row_limit?: number;
  field_count?: number;
  join_count?: number;
  created_at: string;
  updated_at: string;
}

/** A group's grant on a view as the API returns it. */
export interface ViewPermissionRow {
  id: string;
  group_id: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_export: boolean;
  row_scope?: RowScope;
}

export interface ViewField {
  id: number;
  view_id: number;
  column_id: string;
  column?: SchemaColumnResponse;
  table_alias?: string;
  alias?: string;
  display_name?: string;
  visible: boolean;
  editable: boolean;
  is_user_identifier?: boolean;
  // What GET /views/{id}/full actually returns (the keys above are older names that other
  // canvas code still reads).
  source_table_alias?: string | null;
  display_label?: string | null;
  display_order?: number;
  is_visible?: boolean;
  is_editable?: boolean;
  is_link_field?: boolean;
  width_hint?: number | null;
  format_type?: string | null;
  required: boolean;
  sort_order: number;
  width?: number;
  format?: string;
  aggregation?: AggregationType;
}

export type AggregationType = "COUNT" | "SUM" | "AVG" | "MIN" | "MAX" | "GROUP";

export interface ViewJoin {
  id: string;
  view_id: string;
  relationship_id: string;
  parent_join_id: string | null;
  join_type: JoinType;
  table_alias: string | null;
  join_order: number;
  // Included in full response for edit mode
  schema_relationship?: SchemaRelationshipResponse;
  // Legacy fields for backwards compatibility (computed from schema_relationship)
  source_table_id?: string;
  source_column_id?: string;
  target_table_id?: string;
  target_column_id?: string;
  alias?: string;
  sort_order?: number;
}

export type JoinType = "INNER" | "LEFT" | "RIGHT" | "FULL";

export interface ViewFilter {
  id: number;
  view_id: number;
  field_id?: number;
  column_id?: number;
  table_alias?: string;
  operator: FilterOperator;
  value?: unknown;
  is_default: boolean;
  is_required: boolean;
  parameter_name?: string;
}

export interface ViewDefaultFilter {
  id: string;
  view_id: string;
  column_id: string;
  operator: FilterOperator;
  value_json?: { value?: unknown; source_table_alias?: string | null } | null;
  is_locked: boolean;
}

export type FilterOperator =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "like"
  | "ilike"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "in"
  | "not_in"
  | "is_null"
  | "is_not_null"
  | "between";

export const VIEW_DEFAULT_FILTER_OPERATORS: FilterOperator[] = [
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "like",
  "contains",
  "starts_with",
  "ends_with",
  "in",
  "is_null",
  "is_not_null",
];

export interface CanvasViewGridFilterControl {
  field: string;
  label: string;
  operator: "eq" | "ne";
  option_source: "static";
  options: Array<{ label: string; value: string }>;
  default_value?: string | null;
}

export interface ViewSort {
  field_id: number;
  direction: "ASC" | "DESC";
}

// View CRUD types
export interface ViewCreate {
  name: string;
  description?: string;
  focusTableId: string;
  fields?: ViewFieldCreate[];
  joins?: ViewJoinCreate[];
  defaultFilters?: ViewFilterCreate[];
}

/** Rows a group sees on a personal view: its own rows or all rows. */
export type RowScope = "own" | "all";

export interface ViewFieldCreate {
  columnId: string;
  sourceTableAlias?: string;
  displayLabel?: string;
  displayOrder?: number;
  isVisible?: boolean;
  isEditable?: boolean;
  isLinkField?: boolean;
  isUserIdentifier?: boolean;
  widthHint?: number;
  formatType?: string;
}

export interface ViewJoinCreate {
  source_table_id: string;
  source_column_id: string;
  target_table_id: string;
  target_column_id: string;
  join_type: JoinType;
  alias: string;
}

export interface ViewFilterCreate {
  field_id?: number;
  column_id?: number;
  table_alias?: string;
  operator: FilterOperator;
  value?: unknown;
  is_default?: boolean;
  is_required?: boolean;
  parameter_name?: string;
}

export interface ViewUpdate {
  name?: string;
  description?: string;
  row_limit?: number;
  focusTableId?: string;
  isTemplate?: boolean;
  isPublic?: boolean;
  // Nested data for full updates - if provided, replaces all existing records
  fields?: ViewFieldUpdateInView[];
  joins?: ViewJoinUpdateInView[];
  permissions?: ViewPermissionUpdateInView[];
  default_filters?: ViewFilterInlineCreate[];
}

// Update schemas matching backend ViewFieldUpdateInView, ViewJoinUpdateInView, ViewPermissionUpdateInView
export interface ViewFieldUpdateInView {
  columnId: string;
  sourceTableAlias?: string;
  displayLabel?: string;
  displayOrder?: number;
  isVisible?: boolean;
  isEditable?: boolean;
  isLinkField?: boolean;
  isUserIdentifier?: boolean;
  widthHint?: number;
  formatType?: string;
}

export interface ViewJoinUpdateInView {
  relationshipId: string;
  parentJoinId?: string;
  joinType: string;
  tableAlias?: string;
  joinOrder?: number;
}

export interface ViewPermissionUpdateInView {
  groupId: string;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canExport?: boolean;
  rowScope?: RowScope;
}

// ============================================================================
// View Data Types (for ViewGrid)
// ============================================================================

export interface ViewData {
  view: ViewDefinition;
  rows: ViewRow[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface ViewRow {
  __row_id: string | number;
  [key: string]: unknown;
}

export interface ViewRowCreate {
  [key: string]: unknown;
}

export interface ViewRowUpdate {
  [key: string]: unknown;
}

// ============================================================================
// Canvas Types
// ============================================================================

export interface Canvas {
  revision?: number;
  id: number;
  workspace_id: string;
  name: string;
  description?: string;
  /** Legacy field retained for older API responses. */
  canvas_type?: CanvasType;
  archetype?: CanvasArchetype;
  status: CanvasStatus;
  /** Server-authorized access to the assistant for this canvas runtime. */
  canvas_assistant_available?: boolean;
  // Persisted, explicit opt-in wizard authoring flag (spec section on
  // is_wizard; audit R25). Default false: inference never sets it, only a
  // deliberate author choice does. Orthogonal to canvas_type/archetype.
  is_wizard?: boolean;
  elements: CanvasElement[];
  data_sources: CanvasDataSource[];
  settings: CanvasSettings;
  created_at: string;
  updated_at: string;
}

export type CanvasType = "entry" | "browse" | "report" | "dashboard" | "wizard";
export type CanvasArchetype = "data_entry" | "browse" | "report" | "dashboard";
export type CanvasStatus = "draft" | "published" | "archived";

export interface CanvasElement {
  id: string;
  type: CanvasElementType;
  props: CanvasElementProps;
  children?: CanvasElement[];
  data_binding?: CanvasDataBinding;
  layout: CanvasElementLayout;
  visibility_rules?: CanvasVisibilityRule[];
  // Nesting support
  parentElementId?: string; // ID of parent container (undefined = root level)
  parentPanelId?: string; // For Tabs/Accordion: which panel this element belongs to
  displayOrder?: number; // Sort order within parent
}

export type CanvasElementType =
  | "container"
  | "row"
  | "column"
  | "field"
  | "text"
  | "button"
  | "table"
  | "chart"
  | "widget"
  | "image"
  | "divider"
  | "tabs"
  | "accordion"
  | "card"
  | "embedded_canvas"
  | "view_grid"
  | "timeline"
  | "kanban"
  | "calendar"
  | "hierarchy"
  | "related_records"
  | "action_button";

/**
 * Minimum sizes for element types (in grid units).
 */
export const ELEMENT_MIN_SIZES: Partial<
  Record<CanvasElementType, { minRowSpan: number; minColumnSpan: number }>
> = {
  field: { minRowSpan: 1, minColumnSpan: 2 },
  text: { minRowSpan: 1, minColumnSpan: 1 },
  divider: { minRowSpan: 1, minColumnSpan: 4 },
  container: { minRowSpan: 2, minColumnSpan: 4 },
  card: { minRowSpan: 2, minColumnSpan: 3 },
  tabs: { minRowSpan: 3, minColumnSpan: 6 },
  accordion: { minRowSpan: 2, minColumnSpan: 6 },
  embedded_canvas: { minRowSpan: 2, minColumnSpan: 6 },
  view_grid: { minRowSpan: 4, minColumnSpan: 8 },
  timeline: { minRowSpan: 3, minColumnSpan: 4 },
  kanban: { minRowSpan: 4, minColumnSpan: 6 },
  calendar: { minRowSpan: 4, minColumnSpan: 6 },
  hierarchy: { minRowSpan: 4, minColumnSpan: 6 },
  related_records: { minRowSpan: 4, minColumnSpan: 6 },
  action_button: { minRowSpan: 1, minColumnSpan: 2 },
  image: { minRowSpan: 2, minColumnSpan: 2 },
  button: { minRowSpan: 1, minColumnSpan: 2 },
  table: { minRowSpan: 3, minColumnSpan: 6 },
  chart: { minRowSpan: 3, minColumnSpan: 6 },
  widget: { minRowSpan: 2, minColumnSpan: 3 },
};

/**
 * Container subtypes for nested layout elements.
 */
export type ContainerType =
  "SECTION" | "CARD" | "TABS" | "ACCORDION" | "COLUMNS" | "COLLAPSIBLE";

/**
 * Panel definition for Tabs/Accordion containers.
 */
export interface ContainerPanel {
  id: string;
  label: string;
  icon?: string;
}

/**
 * Internal grid configuration for containers.
 * Allows each container to have its own grid settings.
 */
export interface InternalGridConfig {
  columns?: number;
  rowHeight?: number;
  gap?: number;
}

/**
 * Configuration for container elements.
 */
export interface ContainerElementConfig {
  containerType: ContainerType;
  title?: string;
  internalGrid?: InternalGridConfig;
  panels?: ContainerPanel[]; // For TABS/ACCORDION
  defaultPanelId?: string;
  /** Optional presentation; omitted keeps the existing tab appearance. */
  tabStyle?: "default" | "underline";
  isCollapsible?: boolean;
  defaultCollapsed?: boolean;
  selection_binding?: CanvasSelectionBinding;
}

export interface CanvasSelectionBinding {
  source_grid_element_id: string;
  data_source_alias: string;
  mode: "read" | "edit";
  empty_state_text: string;
}

export interface CanvasNavigationConfig {
  target_canvas_id?: string;
  target_view_id?: string;
  record_source?: "current" | "selected" | "none";
  return_to_source?: boolean;
}

export type CanvasNavigationAction = {
  type: "NAVIGATE";
  config: CanvasNavigationConfig;
};

export interface CanvasElementProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  variant?: string;
  size?: "sm" | "md" | "lg";
  color?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  options?: SelectOption[];
  format?: string;
  validation?: FieldValidation[];

  // Text/Static variants
  /** Variant for text/static elements: text (default), heading, html, icon, spacer, image, divider */
  textVariant?:
    "text" | "heading" | "html" | "icon" | "spacer" | "image" | "divider";
  /** Heading level (1-6) for heading variant */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Raw HTML content for html variant */
  htmlContent?: string;
  /** Icon name for icon variant (MUI icon name) */
  iconName?: string;
  /** Height in pixels for spacer variant */
  spacerHeight?: number;
  /** Image URL for image variant (http(s) or relative only) */
  imageUrl?: string;
  /** Snake_case spelling of imageUrl, as stored by older canvases */
  image_url?: string;
  /** Alt text for image variant */
  imageAlt?: string;
  /** Object fit for image variant */
  imageFit?: "contain" | "cover" | "fill" | "none";
  /** Snake_case spelling of imageAlt, as stored by the API */
  image_alt?: string;
  /** Snake_case spelling of imageFit, as stored by the API */
  image_fit?: "contain" | "cover" | "fill" | "none";
  /** Line style for divider variant */
  dividerStyle?: "solid" | "dashed" | "dotted";
  /** Snake_case spelling of dividerStyle, as stored by the API */
  divider_style?: "solid" | "dashed" | "dotted";

  // Text styling
  /** Text color (CSS color value) */
  textColor?: string;
  /** Text alignment */
  textAlign?: "left" | "center" | "right";
  /** Font weight */
  fontWeight?: "normal" | "bold" | "500" | "600" | "700";

  // Field enhancements
  /** Field input type for field elements */
  fieldType?:
    | "text"
    | "number"
    | "date"
    | "datetime"
    | "boolean"
    | "select"
    | "radio"
    | "textarea"
    | "json"
    | "file";
  /** `"text"` shows a field as its label and value as text instead of a disabled control. */
  displayMode?: "text";
  /** Layout of a `radio` field's options (default horizontal). */
  orientation?: "horizontal" | "vertical";

  [key: string]: unknown;
}

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface FieldValidation {
  type: "required" | "min" | "max" | "pattern" | "custom";
  value?: unknown;
  message: string;
}

export interface CanvasDataBinding {
  data_source_id: string;
  field_path: string;
  transform?: string;
}

export interface CanvasElementLayout {
  x?: number;
  y?: number;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  padding?: number | string;
  margin?: number | string;
  flex?: number;
  order?: number;
  // Grid-based positioning (new)
  gridColumn?: number;
  gridRow?: number;
  columnSpan?: number;
  rowSpan?: number;
}

// ============================================================================
// Canvas Grid Types
// ============================================================================

/**
 * Configuration for the canvas grid system.
 */
export interface CanvasGridConfig {
  columns: number; // Number of columns (default 12)
  rowHeight: number; // Height of each row in pixels (default 40)
  gap: number; // Gap between cells in pixels (default 8)
  minRows: number; // Minimum number of rows to display (default 10)
}

/**
 * Default grid configuration.
 */
export const DEFAULT_GRID_CONFIG: CanvasGridConfig = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 10,
};

/**
 * Position on the grid (1-based).
 */
export interface GridPosition {
  row: number;
  column: number;
}

/**
 * Element size on the grid.
 */
export interface GridSize {
  rowSpan: number;
  columnSpan: number;
}

/**
 * Resize direction for resize handles.
 */
export type ResizeDirection = "e" | "s" | "se";

/**
 * State for resize operation.
 */
export interface ResizeState {
  isResizing: boolean;
  direction: ResizeDirection | null;
  startX: number;
  startY: number;
  startColumnSpan: number;
  startRowSpan: number;
}

export interface CanvasVisibilityRule {
  condition: string;
  action: "show" | "hide" | "disable" | "enable";
}

// ============================================================================
// Enhanced Visibility Rules Types
// ============================================================================

/**
 * Operators for visibility conditions.
 */
export type VisibilityOperator =
  | "eq"
  | "ne"
  | "gt"
  | "lt"
  | "gte"
  | "lte"
  | "in"
  | "not_in"
  | "is_empty"
  | "is_not_empty";

/**
 * A single visibility condition.
 */
export interface VisibilityCondition {
  field: string;
  operator: VisibilityOperator;
  value?: unknown;
}

/**
 * Enhanced visibility rules with logic and multiple conditions.
 */
export interface VisibilityRules {
  logic: "and" | "or";
  conditions: VisibilityCondition[];
}

// ============================================================================
// Auto-Fill Configuration Types
// ============================================================================

/**
 * Auto-fill type options.
 */
export type AutoFillType =
  | "current_user"
  | "current_date"
  | "current_datetime"
  | "expression"
  | "lookup";

/**
 * User field options for current_user auto-fill.
 */
export type AutoFillUserField = "id" | "email" | "name" | "entra_id";

/**
 * Configuration for auto-filling field values.
 */
export interface AutoFillConfig {
  type: AutoFillType;
  userField?: AutoFillUserField;
  expression?: string;
  lookupDataSource?: string;
  lookupField?: string;
  lookupFilter?: string;
}

// ============================================================================
// Enhanced Validation Types
// ============================================================================

/**
 * Validation rule type options.
 */
export type ValidationRuleType =
  "required" | "min" | "max" | "minLength" | "maxLength" | "pattern" | "custom";

/**
 * A single validation rule.
 */
export interface ValidationRule {
  type: ValidationRuleType;
  value?: unknown;
  message: string;
}

export interface CanvasDataSource {
  id: string;
  name: string;
  type: DataSourceType;
  view_id?: number | string;
  schema_id?: number | string;
  config: DataSourceConfig;
  /** Replacement-owned fields returned by and written to the canvas API. */
  is_primary?: boolean;
  default_filters?: Record<string, unknown>;
  sort_config?: Record<string, unknown>;
}

export type DataSourceType = "view" | "schema" | "api" | "static";

export interface DataSourceConfig {
  filters?: ViewFilter[];
  parameters?: Record<string, unknown>;
  refresh_interval?: number;
  pagination?: boolean;
  page_size?: number;
}

export interface CanvasSettings {
  theme?: "light" | "dark" | "auto";
  layout?: "fixed" | "responsive";
  submit_button_text?: string;
  success_message?: string;
  redirect_url?: string;
  allow_draft?: boolean;
}

// Canvas CRUD types
// Note: elements and data_sources use unknown[] because the API payload format
// differs from the response format (e.g., element_type vs type, alias field, etc.)
export interface CanvasCreate {
  name: string;
  description?: string;
  canvas_type: CanvasType;
  archetype?: CanvasArchetype;
  // Explicit opt-in only; omitted -> backend default false.
  is_wizard?: boolean;
  elements?: unknown[];
  data_sources?: unknown[];
  settings?: Partial<CanvasSettings>;
}

export interface CanvasUpdate {
  name?: string;
  description?: string;
  archetype?: CanvasArchetype;
  status?: CanvasStatus;
  is_wizard?: boolean;
  elements?: unknown[];
  data_sources?: unknown[];
  settings?: Partial<CanvasSettings>;
}

// Canvas runtime types
export interface CanvasData {
  // Backend returns CanvasDefinitionWithTree; frontend currently renders from /canvases/:id/full instead.
  canvas: unknown;
  record?: Record<string, unknown> | null;
  records?: Record<string, unknown>[] | null;
  record_id?: string | null;
  record_label?: string | null;
  field_metadata?: unknown[];
  can_edit: boolean;
  can_create: boolean;
  can_delete: boolean;
  record_count?: number | null;
  current_index?: number | null;
  has_more?: boolean;
}

export interface CanvasPermissions {
  can_view: boolean;
  can_edit: boolean;
  can_submit: boolean;
  can_delete: boolean;
}

export interface CanvasSubmitResult {
  success: boolean;
  message?: string;
  errors?: Record<string, string[]>;
  created_ids?: Record<string, number | string>;
}

// ============================================================================
// Magic Bridge Types (N-level FK resolution)
// ============================================================================

export interface MagicBridgeOptions {
  source_table: string;
  source_column: string;
  display_columns: string[];
  search_columns?: string[];
  filter?: Record<string, unknown>;
  limit?: number;
}

export interface MagicBridgeResult {
  value: string | number;
  display: string;
  data: Record<string, unknown>;
}

// ============================================================================
// Permission Types
// ============================================================================

export interface ViewPermission {
  id: number;
  view_id: number;
  role_id?: number;
  user_id?: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
}

export interface CanvasPermission {
  id: number;
  canvas_id: number;
  role_id?: number;
  user_id?: string;
  can_view: boolean;
  can_execute: boolean;
  can_edit: boolean;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiError {
  detail: string;
  code?: string;
  field?: string;
}

// ============================================================================
// File Field Types
// ============================================================================

/**
 * Reference to a file stored in blob storage.
 * Stored as JSON in NVARCHAR(MAX) columns.
 */
export interface FileReference {
  outlet: string;
  container: string;
  path: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  uploaded_by: string;
}

/**
 * Format a byte count as a human-readable file size string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

// ============================================================================
// Schema Builder Types
// ============================================================================

/**
 * User-friendly column type for the schema builder UI.
 * Maps to SQL Server types in the backend.
 */
export type SimpleColumnType =
  "Text" | "Number" | "Date" | "Yes/No" | "Selection" | "File";

/**
 * Maps user-friendly types to SQL Server types (for reference).
 */
export const SIMPLE_TYPE_SQL_MAPPING: Record<SimpleColumnType, string> = {
  Text: "NVARCHAR(255)",
  Number: "DECIMAL(18, 4)",
  Date: "DATE",
  "Yes/No": "BIT",
  Selection: "NVARCHAR(100)",
  File: "NVARCHAR(MAX)",
};

/**
 * Process schema configuration for a workspace.
 */
export interface ProcessSchemaConfig {
  id: string;
  workspace_id: string;
  schema_name: string;
  is_initialized: boolean;
  initialized_at?: string;
  initialized_by?: string;
  schema_version: number;
}

/**
 * Schema initialization status response.
 */
export interface SchemaStatus {
  workspace_id: string;
  is_initialized: boolean;
  schema_name?: string;
  table_count: number;
}

/**
 * API column type values (uppercase for backend compatibility).
 */
export type ApiColumnType =
  "TEXT" | "NUMBER" | "DATE" | "BOOLEAN" | "SELECT" | "FILE";

/**
 * Column definition for the schema builder API requests.
 */
export interface ColumnDefinition {
  name: string;
  display_name: string;
  simple_type: ApiColumnType;
  is_nullable: boolean;
  is_primary_key: boolean;
  select_options?: string[];
  description?: string;
  default_value?: string;
}

/**
 * Request to create a new table.
 */
export interface CreateTableRequest {
  name: string;
  display_name: string;
  description?: string;
  columns: ColumnDefinition[];
}

/**
 * Request to alter table metadata.
 */
export interface AlterTableRequest {
  display_name?: string;
  description?: string;
}

/**
 * Request to add a column to a table.
 */
export interface AddColumnRequest {
  column: ColumnDefinition;
}

/**
 * Request to alter a column.
 */
export interface AlterColumnRequest {
  display_name?: string;
  simple_type?: ApiColumnType;
  is_nullable?: boolean;
  select_options?: string[];
  description?: string;
}

/**
 * Request to create a foreign key relationship.
 */
export interface CreateRelationshipRequest {
  from_table_id: string;
  from_column_name: string;
  to_table_id: string;
  to_column_name: string;
  relationship_name?: string;
  on_delete?: OnDeleteBehavior;
}

/**
 * Schema table with columns response.
 */
export interface SchemaTableWithColumns {
  id: string;
  schema_name: string;
  table_name: string;
  display_name: string;
  description?: string;
  primary_key_column?: string;
  columns: SchemaColumnResponse[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Schema column response from API.
 */
export interface SchemaColumnResponse {
  id: string;
  table_id: string;
  column_name: string;
  display_name: string;
  data_type: string;
  is_nullable: boolean;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  is_available: boolean;
  max_length?: number;
}

/**
 * Schema relationship response from API.
 */
export interface SchemaRelationshipResponse {
  id: string;
  workspace_id: string;
  from_table_id: string;
  from_column_id: string;
  to_table_id: string;
  to_column_id: string;
  relationship_name?: string;
  relationship_type: string;
  on_delete?: OnDeleteBehavior;
}

// ============================================================================
// Schema Builder State Types (for UI)
// ============================================================================

/**
 * Table definition for the schema builder UI state.
 */
export interface SchemaBuilderTable {
  tempId: string;
  id?: string;
  name: string;
  displayName: string;
  description: string;
  columns: SchemaBuilderColumn[];
}

/**
 * Column definition for the schema builder UI state.
 */
export interface SchemaBuilderColumn {
  tempId: string;
  id?: string;
  name: string;
  displayName: string;
  dataType: SimpleColumnType;
  isPrimaryKey: boolean;
  isRequired: boolean;
  selectOptions?: string[];
  sortOrder: number;
}

/**
 * Relationship definition for the schema builder UI state.
 */
export type OnDeleteBehavior = "RESTRICT" | "CASCADE" | "SET_NULL";

export interface SchemaBuilderRelationship {
  tempId: string;
  id?: string;
  sourceTableId: string;
  sourceColumnId: string;
  targetTableId: string;
  targetColumnId: string;
  displayName: string;
  onDelete: OnDeleteBehavior;
}

/**
 * Full schema builder state.
 */
export interface SchemaBuilderState {
  tables: SchemaBuilderTable[];
  selectedTableId: string | null;
  relationships: SchemaBuilderRelationship[];
  pendingTableDeletions: string[];
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
}

// ============================================================================
// View Builder Types (5-Step Stepper Workflow)
// ============================================================================

/**
 * Join definition for the view builder UI state.
 * Supports hierarchical/nested joins via parentJoinId.
 */
export interface ViewBuilderJoin {
  id: string;
  relationshipId: string;
  relationship: SchemaRelationshipResponse | null; // May be null when loading from API
  parentJoinId: string | null; // null = joins directly to focus table
  joinType: JoinType;
  alias: string;
  joinOrder: number;
  // Derived/display properties
  sourceTableName: string;
  sourceColumnName: string;
  targetTableId: string;
  targetTableName: string;
  targetColumnName: string;
}

/**
 * Field definition for the view builder UI state.
 * Uses tableAlias for unique identification when same table is joined multiple times.
 */
export interface ViewBuilderField {
  id: string;
  columnId: string;
  column: SchemaColumnResponse;
  tableId: string;
  tableName: string;
  tableAlias: string | null; // null = focus table, otherwise join alias
  displayLabel: string;
  isVisible: boolean;
  isEditable: boolean;
  isLinkField: boolean; // For FK reference editing mode
  isUserIdentifier: boolean; // Marks the field that identifies the signed-in user
  sortOrder: number;
  widthHint?: number;
  formatType?: string;
}

/**
 * Filter definition for the view builder UI state.
 */
export interface ViewBuilderFilter {
  id: string;
  columnId: string;
  tableAlias: string | null;
  operator: FilterOperator;
  value: unknown;
  valueJson?: Record<string, unknown>;
  isLocked: boolean; // Locked filters cannot be changed by users
}

/**
 * Permission definition for the view builder UI state.
 */
export interface ViewBuilderPermission {
  id: string;
  groupId: string;
  groupName?: string;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  rowScope: RowScope;
}

/**
 * Full view builder state.
 */
export interface ViewBuilderState {
  // Identity
  viewId: string | null;
  isEditing: boolean;

  // Step 1: Basic Info & Focus Table
  name: string;
  description: string;
  focusTableId: string | null;
  focusTable: SchemaTableWithColumns | null;
  isTemplate: boolean;
  isPublic: boolean;

  // Step 2: Joins
  relationships: SchemaRelationshipResponse[];
  selectedJoins: ViewBuilderJoin[];

  // Step 3: Fields
  selectedFields: ViewBuilderField[];

  // Step 4: Filters
  filters: ViewBuilderFilter[];

  // Step 5: Permissions
  permissions: ViewBuilderPermission[];

  // Navigation
  currentStep: number;

  // UI State
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  validationErrors: Record<string, string>; // Per-field validation errors
}

/**
 * View builder step definition.
 */
export interface ViewBuilderStep {
  id: string;
  label: string;
  description?: string;
}

/**
 * View builder steps configuration.
 */
export const VIEW_BUILDER_STEPS: ViewBuilderStep[] = [
  {
    id: "focus",
    label: "Focus Table",
    description: "Select the primary table for this view",
  },
  {
    id: "joins",
    label: "Joins",
    description: "Add related tables via relationships",
  },
  {
    id: "fields",
    label: "Visible Fields",
    description: "Select and order the fields to display",
  },
  { id: "filters", label: "Filters", description: "Configure default filters" },
  {
    id: "permissions",
    label: "Permissions",
    description: "Set access permissions",
  },
];

/**
 * Operators available for each data type in filters.
 */
export const FILTER_OPERATORS_BY_TYPE: Record<string, FilterOperator[]> = {
  string: [
    "eq",
    "ne",
    "like",
    "ilike",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "in",
    "not_in",
    "is_null",
    "is_not_null",
  ],
  text: [
    "eq",
    "ne",
    "like",
    "ilike",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_null",
    "is_not_null",
  ],
  integer: [
    "eq",
    "ne",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "in",
    "not_in",
    "is_null",
    "is_not_null",
  ],
  decimal: [
    "eq",
    "ne",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "is_null",
    "is_not_null",
  ],
  boolean: ["eq", "ne", "is_null", "is_not_null"],
  date: [
    "eq",
    "ne",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "is_null",
    "is_not_null",
  ],
  datetime: [
    "eq",
    "ne",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "is_null",
    "is_not_null",
  ],
  uuid: ["eq", "ne", "in", "not_in", "is_null", "is_not_null"],
};

/**
 * System columns to filter out from joined tables.
 */
export const SYSTEM_COLUMNS = [
  "created_at",
  "updated_at",
  "created_by",
  "updated_by",
  "__row_id",
];

/**
 * Check if a column is a system column.
 */
export function isSystemColumn(columnName: string): boolean {
  const lowerName = columnName.toLowerCase();
  return SYSTEM_COLUMNS.some(
    (sys) => lowerName === sys || lowerName.endsWith(`_${sys}`),
  );
}

/**
 * API payload for creating a view with all nested data.
 */
export interface ViewCreateFull {
  name: string;
  description?: string;
  focus_table_id: string;
  is_template?: boolean;
  is_public?: boolean;
  fields: ViewFieldInlineCreate[];
  joins?: ViewJoinInlineCreate[];
  default_filters?: ViewFilterInlineCreate[];
  permissions?: ViewPermissionInlineCreate[];
}

export interface ViewFieldInlineCreate {
  column_id: string;
  source_table_alias?: string;
  display_label?: string;
  display_order: number;
  is_visible: boolean;
  is_editable: boolean;
  is_link_field?: boolean;
  is_user_identifier?: boolean;
  width_hint?: number;
  format_type?: string;
}

export interface ViewJoinInlineCreate {
  relationship_id: string;
  parent_join_order?: number; // Reference parent by join_order, not ID
  join_type: JoinType;
  table_alias: string;
  join_order: number;
}

export interface ViewFilterInlineCreate {
  field: string; // Field name in format "table_alias.column_name"
  operator: string;
  value: unknown; // Filter value (required by backend)
}

export interface ViewPermissionInlineCreate {
  group_id: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_export: boolean;
  row_scope: RowScope;
}

// ============================================================================
// Magic Bridge Types (Re-export)
// ============================================================================

export * from "./magicBridge";
